'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import styled from 'foundations-theming/utils';
import I18n from 'I18n';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { config } from 'sanitize-text/sanitizers/SanitizeConfiguration';
import { contains, isEqual, findWhere } from 'underscore';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import { FileInput } from 'foundations-components/transitional/components/FileInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { Small } from 'foundations-components/transitional/components/Small';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import { Select } from 'foundations-components/transitional/components/Select';
import { FieldTypes } from 'forms-utils-lib/constants/formField/FieldTypeMapping';
import FormFieldInputLabelContainer from 'FormsUI/components/editor/form/formFields/FormFieldInputLabelContainer';
import { getOptionsForSelect, getMetaDataValue } from 'FormsUI/utils/FormFieldsUtils';
import { formatDateForDateInput, getFormatTextFromMetaData } from 'FormsUI/utils/DateUtils';
import { isContactProperty } from 'FormsCommonComponents/utils/PropertyObjectTypes';
import { getObjectTypeIdValue } from 'FormsCommonComponents/form-definition/utils/FormFieldUtils';
import FormFieldErrorMessage from 'FormsUI/components/editor/form/formFields/FormFieldErrorMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledFieldLabel = styled.span.withConfig({
  displayName: "FormFieldInput__StyledFieldLabel"
})(["", ""], props => props.isFieldRequired && `&::after {content: '\\0000a0*'}`);
const DraggingDiv = styled.div.withConfig({
  displayName: "FormFieldInput__DraggingDiv"
})(["opacity:", ";"], props => props.isDragging ? 0.3 : 1);
export default class FormFieldInput extends PureComponent {
  getFieldLabel() {
    const {
      formField: {
        label,
        name: fieldName,
        required: isFieldRequired,
        fieldType
      },
      isFormLabelPropertyEnabled
    } = this.props;
    if (fieldType === FieldTypes.SINGLE_CHECKBOX) {
      return null;
    }
    const id = isFieldRequired ? {
      id: 'required-form-field'
    } : {};
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsxs(StyledFieldLabel, Object.assign({}, id, {
        isFieldRequired: isFieldRequired,
        children: [/*#__PURE__*/_jsx(SanitizedText, {
          body: label
        }), isFormLabelPropertyEnabled ? /*#__PURE__*/_jsxs(Small, {
          use: "help",
          children: [" (", fieldName, ")"]
        }) : null]
      }))
    );
  }
  getNumberFieldValue(defaultValue) {
    if (Number(defaultValue) === 0) return 0;
    if (!defaultValue) return undefined;
    return Number(defaultValue);
  }
  renderPhoneInput(formField) {
    const useCountryCodeSelect = getMetaDataValue(formField.metaData, 'useCountryCodeSelect');
    const objectTypeId = getObjectTypeIdValue(formField.objectTypeId, formField.propertyObjectType);
    if (!useCountryCodeSelect || formField.name !== FieldTypes.PHONE || !isContactProperty(objectTypeId)) {
      return this.renderTextInput(formField);
    }
    return /*#__PURE__*/_jsxs(TFlex, {
      children: [/*#__PURE__*/_jsx(Box, {
        paddingInlineEnd: "200",
        "data-test-id": "country-code-selector",
        basis: 150,
        children: this.renderSelectInput({
          unselectedLabel: ' ',
          options: [],
          selectedOptions: []
        })
      }), /*#__PURE__*/_jsx(Box, {
        grow: 1,
        children: this.renderTextInput(formField)
      })]
    });
  }
  renderTextInput(formField) {
    return /*#__PURE__*/_jsx(TextInput, {
      "data-test-id": "form-field-input",
      id: formField.label,
      use: "on-dark",
      placeholder: sanitize(formField.placeholder, config.TEXTONLY),
      defaultValue: formField.defaultValue
    });
  }
  renderCheckbox(formField) {
    const numColumns = parseInt(getMetaDataValue(formField.metaData, 'numColumns'), 10);
    const {
      hasPropertyAnnotationsHintsEnabled
    } = this.props;
    const checkboxes = formField.options.map(option => {
      return /*#__PURE__*/_jsx(GridItem, {
        size: {
          xs: 12 / (numColumns || 1)
        },
        paddingBlockEnd: numColumns ? '200' : undefined,
        children: /*#__PURE__*/_jsx(Checkbox, {
          checked: contains(formField.selectedOptions, option.value.toString()),
          children: /*#__PURE__*/_jsx(BreakString, {
            hyphenate: false,
            children: /*#__PURE__*/_jsx(FormFieldInputLabelContainer, {
              hasPropertyAnnotationsHintsEnabled: hasPropertyAnnotationsHintsEnabled,
              name: option.value,
              fieldType: formField.fieldType,
              fieldLabel: sanitize(option.label, config.TEXTONLY)
            })
          })
        })
      }, option.value);
    });
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Grid, {
        children: checkboxes
      })
    });
  }
  renderSingleCheckbox(formField) {
    const {
      hasPropertyAnnotationsHintsEnabled
    } = this.props;
    return /*#__PURE__*/_jsx(Checkbox, {
      checked: contains(formField.selectedOptions, 'true'),
      children: /*#__PURE__*/_jsx(FormFieldInputLabelContainer, {
        hasPropertyAnnotationsHintsEnabled: hasPropertyAnnotationsHintsEnabled,
        name: formField.name,
        fieldType: formField.fieldType,
        objectTypeId: formField.objectTypeId,
        fieldLabel: sanitize(formField.label, config.TEXTONLY),
        required: formField.required,
        hidden: formField.hidden
      })
    });
  }
  renderRadioButtons(formField) {
    const selectedOption = formField.selectedOptions[0];
    const selectedKey = `${formField.name}_${selectedOption}`;
    const radioButtons = formField.options.map(option => {
      const key = `${formField.name}_${option.value}`;
      return /*#__PURE__*/_jsx(RadioInput, {
        name: key,
        size: "small",
        checked: isEqual(key, selectedKey),
        children: sanitize(option.label, config.TEXTONLY)
      }, key);
    });
    return /*#__PURE__*/_jsx("div", {
      children: radioButtons
    });
  }
  renderSelectInput(formField) {
    const options = getOptionsForSelect(formField.options);
    const defaultValue = findWhere(options, {
      value: formField.selectedOptions[0]
    });
    return /*#__PURE__*/_jsx(Select, {
      options: options,
      buttonUse: "form-on-dark",
      placeholder: formField.unselectedLabel || I18n.text('editor.form.formFields.defaultSelectOptionLabel'),
      value: defaultValue ? sanitize(defaultValue.value, config.TEXTONLY) : undefined
    });
  }
  renderDateInput(formField) {
    const date = formatDateForDateInput(formField.defaultValue);
    return /*#__PURE__*/_jsx(DateInput, {
      format: getFormatTextFromMetaData(formField),
      use: "on-dark",
      value: date
    }, getFormatTextFromMetaData(formField));
  }
  renderTextAreaInput(formField) {
    return /*#__PURE__*/_jsx(TextArea, {
      marginBlockEnd: "400",
      placeholder: sanitize(formField.placeholder, config.TEXTONLY),
      value: formField.defaultValue,
      use: "on-dark",
      resize: false
    });
  }
  renderNumberField(formField) {
    return /*#__PURE__*/_jsx(NumberInput, {
      formatter: value => value,
      value: this.getNumberFieldValue(formField.defaultValue),
      use: "on-dark",
      placeholder: sanitize(formField.placeholder, config.TEXTONLY),
      type: "number"
    });
  }
  renderFileUploader(formField) {
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(FileInput, {
        use: formField.name ? 'tertiary-light' : 'danger'
      })
    });
  }
  renderHelpText() {
    return /*#__PURE__*/_jsx(SanitizedText, {
      body: this.props.formField.description
    });
  }
  renderInput() {
    const {
      formField
    } = this.props;
    const isPhoneInput = formField.name === FieldTypes.PHONE;
    switch (formField.fieldType) {
      case FieldTypes.TEXT:
      case FieldTypes.PHONE_NUMBER:
        return isPhoneInput ? this.renderPhoneInput(formField) : this.renderTextInput(formField);
      case FieldTypes.CHECKBOX:
        return this.renderCheckbox(formField);
      case FieldTypes.SINGLE_CHECKBOX:
        return this.renderSingleCheckbox(formField);
      case FieldTypes.RADIO:
        return this.renderRadioButtons(formField);
      case FieldTypes.SELECT:
        return this.renderSelectInput(formField);
      case FieldTypes.DATE:
        return this.renderDateInput(formField);
      case FieldTypes.TEXTAREA:
        return this.renderTextAreaInput(formField);
      case FieldTypes.NUMBER:
        return this.renderNumberField(formField);
      case FieldTypes.PAYMENT_RADIO:
        return this.renderRadioButtons(formField);
      case FieldTypes.FILE:
        return this.renderFileUploader(formField);
      default:
        return this.renderTextInput(formField);
      // default as text, some fields don't have a fieldType due to probably a past bug creating form fields, ex: FORMS-2008
    }
  }
  render() {
    const {
      formField,
      isBeingDragged,
      hasProgressiveProfilling,
      hasPropertyAnnotationsHintsEnabled,
      formFieldError
    } = this.props;
    const shouldShowProfilingStatus = !(!formField.isSmartField || !hasProgressiveProfilling);
    return /*#__PURE__*/_jsx(Fieldset, {
      "data-test-id": "form-field-fieldset",
      children: /*#__PURE__*/_jsx(DraggingDiv, {
        isDragging: isBeingDragged,
        children: /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(MediaObject, {
            inlineSize: "100%",
            minBlockSize: formField.isSmartField ? '28px' : undefined,
            children: formField.fieldType !== FieldTypes.SINGLE_CHECKBOX && /*#__PURE__*/_jsx(FormFieldInputLabelContainer, {
              hasPropertyAnnotationsHintsEnabled: hasPropertyAnnotationsHintsEnabled,
              name: formField.name,
              fieldType: formField.fieldType,
              objectTypeId: formField.objectTypeId,
              fieldLabel: this.getFieldLabel(),
              hidden: formField.hidden,
              shouldShowProfilingStatus: shouldShowProfilingStatus
            })
          }),
          help: this.renderHelpText(),
          inlineSize: "100%",
          paddingBlock: "0",
          paddingInline: "200",
          "data-test-id": "form-field",
          "data-onboarding": "form-field",
          error: !!formFieldError,
          validationMessage: formFieldError && /*#__PURE__*/_jsx(FormFieldErrorMessage, {
            formFieldError: formFieldError
          }),
          children: this.renderInput()
        })
      })
    });
  }
}
FormFieldInput.propTypes = {
  formField: PropTypes.object.isRequired,
  isBeingDragged: PropTypes.bool.isRequired,
  isFormLabelPropertyEnabled: PropTypes.bool,
  hasProgressiveProfilling: PropTypes.bool.isRequired,
  hasPropertyAnnotationsHintsEnabled: PropTypes.bool.isRequired,
  formFieldError: PropTypes.object
};